var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { store } from 'store/store';
import TRTC from 'trtc-js-sdk';
import * as ConversationActions from 'reducers/conversation';
export class TrtcClient {
    constructor() {
        this.REMOTE_STREAM_DOM_ID = 'remoteStream';
        this.LOCAL_STREAM_DOM_ID = 'localStream';
        this.STATUS_CONNECTED = 'CONNECTED';
        this.isAudio = true;
        this.isVideo = true;
        this.isRemoteJoined = false;
        this.MODE_RTC = 'rtc';
        this.isConnected = false;
        this.isConnecting = false;
        this.isInitialized = false;
        this.isInitializing = false;
    }
    static getInstance() {
        if (!TrtcClient.instance) {
            TrtcClient.instance = new TrtcClient();
        }
        return TrtcClient.instance;
    }
    set onStreamAdded(callback) {
        this._onStreamAdded = callback;
    }
    set onStreamRemoved(callback) {
        this._onStreamRemoved = callback;
    }
    set onRemoteMuteAudio(callback) {
        this._onRemoteMuteAudio = callback;
    }
    set onRemoteUnmuteAudio(callback) {
        this._onRemoteUnmuteAudio = callback;
    }
    set onRemoteMuteVideo(callback) {
        this._onRemoteMuteVideo = callback;
    }
    set onRemoteUnmuteVideo(callback) {
        this._onRemoteUnmuteVideo = callback;
    }
    set onConnected(callback) {
        this._onConnected = callback;
    }
    UnmuteRemoteVideo() {
        var _a;
        (_a = this.remoteStream) === null || _a === void 0 ? void 0 : _a.unmuteVideo();
    }
    addEventsListener() {
        if (!this.client) {
            return;
        }
        this.client.on('stream-added', (event) => {
            var _a;
            const remoteStream = event.stream;
            (_a = this.client) === null || _a === void 0 ? void 0 : _a.subscribe(remoteStream);
            if (this._onStreamAdded) {
                this._onStreamAdded();
            }
        });
        this.client.on('stream-subscribed', (event) => {
            const remoteStream = event.stream;
            this.remoteStream = remoteStream;
            remoteStream.play(this.REMOTE_STREAM_DOM_ID);
        });
        this.client.on('stream-removed', (event) => {
            const { stream: remoteStream } = event;
            remoteStream.stop();
            if (this._onStreamRemoved) {
                this._onStreamRemoved();
            }
        });
        this.client.on('connection-state-changed', (event) => {
            const curState = event.state;
            if (curState === this.STATUS_CONNECTED && this._onConnected) {
                this._onConnected();
            }
        });
        this.client.on('mute-audio', (event) => {
            if (this._onRemoteMuteAudio) {
                this._onRemoteMuteAudio();
            }
        });
        this.client.on('unmute-audio', (event) => {
            if (this._onRemoteUnmuteAudio) {
                this._onRemoteUnmuteAudio();
            }
        });
        this.client.on('mute-video', (event) => {
            if (this._onRemoteMuteVideo) {
                this._onRemoteMuteVideo();
            }
        });
        this.client.on('unmute-video', (event) => {
            if (this._onRemoteUnmuteVideo) {
                this._onRemoteUnmuteVideo();
            }
        });
        this.client.on('network-quality', (event) => {
            const { uplinkNetworkQuality, downlinkNetworkQuality } = event;
            store.dispatch(ConversationActions.setNetworkQuality(uplinkNetworkQuality));
            store.dispatch(ConversationActions.setPartnerNetworkQuality(downlinkNetworkQuality));
        });
    }
    init(tencentSdkInfo, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isInitialized || this.isInitializing) {
                return;
            }
            this.userId = userId;
            this.isInitialized = false;
            this.isInitializing = true;
            this.client = TRTC.createClient({
                mode: this.MODE_RTC,
                sdkAppId: Number(tencentSdkInfo.tencent_sdk_app_id),
                userId,
                useStringRoomId: true,
                userSig: tencentSdkInfo.user_sig,
            });
            this.addEventsListener();
            this.isInitialized = true;
            this.isInitializing = false;
        });
    }
    connect(roomId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.client || this.isConnected || this.isConnecting) {
                return;
            }
            this.isConnecting = true;
            this.roomId = roomId;
            try {
                yield this.client.join({ roomId: this.roomId });
            }
            catch (error) {
                this.isConnecting = false;
                throw error;
            }
            this.localStream = TRTC.createStream({
                audio: this.isAudio,
                video: this.isVideo,
                userId: this.userId,
            });
            try {
                if (this.localStream) {
                    yield this.localStream.initialize();
                }
                else {
                    throw new Error(TrtcClient.INITIAL_STREAM_ERROR);
                }
                if (this.localStream) {
                    yield this.client.publish(this.localStream);
                    this.localStream.play(this.LOCAL_STREAM_DOM_ID);
                    this.isConnected = true;
                }
            }
            finally {
                this.isConnecting = false;
            }
        });
    }
    setVideoMute(mute) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (mute) {
                (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.muteVideo();
            }
            else {
                (_b = this.localStream) === null || _b === void 0 ? void 0 : _b.unmuteVideo();
            }
        });
    }
    setAudioMute(mute) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (mute) {
                (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.muteAudio();
            }
            else {
                (_b = this.localStream) === null || _b === void 0 ? void 0 : _b.unmuteAudio();
            }
        });
    }
    destroyLocalStream() {
        var _a, _b;
        (_a = this.localStream) === null || _a === void 0 ? void 0 : _a.stop();
        (_b = this.localStream) === null || _b === void 0 ? void 0 : _b.close();
        this.localStream = undefined;
        this._onConnected = undefined;
        this._onStreamAdded = undefined;
        this._onStreamRemoved = undefined;
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.localStream) {
                yield ((_a = this.client) === null || _a === void 0 ? void 0 : _a.unpublish(this.localStream));
                this.destroyLocalStream();
            }
            yield ((_b = this.client) === null || _b === void 0 ? void 0 : _b.leave());
            this.client = undefined;
            this.isConnected = false;
            this.isInitialized = false;
        });
    }
    reconnect(tencentSdkInfo, userId, channelId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.disconnect();
            yield this.init(tencentSdkInfo, userId);
            yield this.connect(channelId);
        });
    }
}
TrtcClient.ENTER_ROOM_ERROR = 'enter_room_error';
TrtcClient.PUBLISH_STREAM_ERROR = 'publish_stream_error';
TrtcClient.INITIAL_STREAM_ERROR = 'initial_stream_error';
export const isConnectionError = (e) => {
    return (e instanceof Error &&
        (e.message === TrtcClient.ENTER_ROOM_ERROR ||
            e.message === TrtcClient.INITIAL_STREAM_ERROR ||
            e.message === TrtcClient.PUBLISH_STREAM_ERROR));
};
